import { v4 as uuidv4 } from 'uuid';

import Tasks from 'APP/Tasks';
import { getKeyPackages } from 'APP/Tasks/e2ee/getKeyPackages';
import { IGroupCounterView } from 'APP/model/counter/counterModel.types';
import { createSecretGroupAndInvite as apiCreateSecretGroupAndInvite } from 'APP/model/e2ee/e2eeModel';
import { ICreateGroupRequest, IPersonalizedGroup } from 'APP/model/group/groupModel.types';
import { spaceAnalytics } from 'APP/packages/analytics/analytics';
import { Group } from 'APP/store/Groups/Group';
import { MlsStorageService } from 'SERVICES/mlsStorageService/MlsStorageService';
import { IAddMembersOutput } from 'SERVICES/mlsStorageService/MlsStorageService.types';
import Entities from 'STORE';
import { SecretGroup } from 'STORE/Groups/SecretGroup/SecretGroup';

async function request(
  data: ICreateGroupRequest,
  keyPackages: IAddMembersOutput
): Promise<null | { group: IPersonalizedGroup; counter: IGroupCounterView }> {
  try {
    // SERVER IS SENSITIVE TO DEFAULT VALUES, DON'T CHANGE THEM
    const response = await apiCreateSecretGroupAndInvite({
      name: (data.name || '').trim(),
      description: data.description || '',
      adminsWelcomeMessages: [],
      avatarUrl: data.avatarUrl || undefined,
      participantsWelcomeMessages: keyPackages.welcomeMessages,
      commit: keyPackages.commit,
      epoch: keyPackages.epoch,
    });

    if (response?.personalizedGroup?.groupResponse) {
      return {
        group: response.personalizedGroup,
        counter: response.groupCounter,
      };
    }
  } catch (error) {
    return null;
  }

  return null;
}

function trackGroupCreateEvent(group: Group): void {
  if (!group.spaceId) {
    return;
  }

  const space = Entities.spacesStore.getById(group.spaceId);

  if (space) {
    group.isChatGroup
      ? spaceAnalytics.trackSpaceGroupCreated(space.id, space.type, group.id, group.type)
      : spaceAnalytics.trackSpaceChannelCreated(space.id, space.type, group.id, group.type);
  }
}

export async function createSecretGroupAndInvite(data: ICreateGroupRequest): Promise<Group | null> {
  const members = data.members || [];
  const keyPackagesResponse = await getKeyPackages([...members, Entities.usersStore.Me.id]);
  const keyPackages = keyPackagesResponse.keyPackages;

  const groupUuid = uuidv4();
  const mlsGroup = MlsStorageService.createGroup(groupUuid);

  const addMembersResult = MlsStorageService.addMembersToGroup(keyPackages, mlsGroup);

  MlsStorageService.processMyWelcomeCommit(addMembersResult.welcomeCommit, mlsGroup);
  const response = await request(data, addMembersResult);

  if (!response) {
    return null;
  }

  const { group, counter } = response;

  MlsStorageService.chainGroupIdAndUuid(group.groupResponse.id, groupUuid);
  const opponent = group.opponent || group.botOpponent;
  if (opponent) {
    Entities.usersStore.add([opponent]);
  }

  Entities.GroupsStore.merge([group]);

  const newGroup = Entities.GroupsStore.getGroupById(group.groupResponse.id) as SecretGroup;
  if (!newGroup) {
    return null;
  }

  newGroup.setIsUnlocked();
  await Tasks.group.setActiveGroup({ groupId: newGroup.id });

  await Tasks.group.addCounters([counter]);

  trackGroupCreateEvent(newGroup);

  return newGroup;
}
